import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import { useTracking } from 'react-tracking';
import { useOnClickOutside } from '../../../hooks';
import UsersAutocomplete from '../../../../../common/autocomplete/users';
import SearchUserDropdown from './search-types-dropdown';
import { SearchIcon } from '../../../../../../icons';

type SearchHandlerFunction = (value?: string, type?: string) => any;

interface Props {
  isExpanded: boolean;
  searchTypes: string[];
  currentSearchType: string;
  searchValue: string;
  onSearch: (props: { type: string; value: string; userId?: string }) => void;
  placeholder?: string;
  suggestions?: any[];
  onSearchValueChange?: SearchHandlerFunction;
  onSearchTypeChange?: SearchHandlerFunction;
  onCloseSearch?: (withReset?: boolean) => void;
  onOpenSearch?: () => void;
  cancelSearch?: SearchHandlerFunction;
  onSuggestionClick?: SearchHandlerFunction;
  clearSuggestions?: SearchHandlerFunction;
}

const Search: React.FC<Props> = ({
  isExpanded = false,
  searchTypes = [],
  searchValue = '',
  currentSearchType,
  onSearchTypeChange,
  onSearchValueChange,
  onSearch,
  onCloseSearch,
  onOpenSearch,
  cancelSearch,
  placeholder,
  suggestions,
  onSuggestionClick,
  clearSuggestions
}) => {
  const searchInputRef = useRef<HTMLInputElement>();
  const suggestionTooltipRef = useRef<HTMLDivElement>();
  const isEmptySearchValue = searchValue.length < 1;
  const tracking = useTracking();
  useOnClickOutside(suggestionTooltipRef, clearSuggestions);

  const onTypeChange = (searchType: string) => {
    onSearchTypeChange(searchValue, searchType);
    focusSearchInput();
  };

  const onValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue: string = e.currentTarget.value;
    onSearchValueChange(newSearchValue, currentSearchType);
  };

  const onValueKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key: number = e.charCode;
    if (key === 13 && !isEmptySearchValue) {
      const searchedUserId = suggestions.length && suggestions[0].userId;
      search(true, searchedUserId || null);
    }
  };

  const search = (fromKeyboard: boolean = false, userId?: string) => {
    if (!isEmptySearchValue) {
      onCloseSearch();
      tracking.trackEvent({
        eventName: 'moderation search results clicked',
        click_type: fromKeyboard ? 'enter' : 'mouse'
      });
      onSearch({ value: searchValue, type: currentSearchType, userId });
    }
  };

  const focusSearchInput = () => {
    searchInputRef?.current?.focus();
  };

  useEffect(() => {
    focusSearchInput();

    if (suggestions.length) {
      clearSuggestions();
    }
  }, [isExpanded]);

  return (
    <span className={classnames('search', { expanded: isExpanded })}>
      {!isExpanded ? (
        <>
          <span className={'search-bar'}>
            <SearchIcon className={'search-icon'} />
            <span
              className={classnames('search-text', { ['result']: searchValue })}
              onClick={onOpenSearch}
            >
              {searchValue ? searchValue : 'Search...'}
            </span>
            {searchValue && (
              <span className={'regular-link'} onClick={cancelSearch}>
                Cancel
              </span>
            )}
          </span>
        </>
      ) : (
        <>
          <SearchIcon className={'search-icon'} />

          {searchTypes.length > 1 && (
            <SearchUserDropdown
              searchTypes={searchTypes}
              currentSearchType={currentSearchType}
              onChangeSearchType={onTypeChange}
            />
          )}
          <span className={'search-input-container'}>
            <input
              ref={searchInputRef}
              maxLength={currentSearchType === 'Message ID' ? 500 : 40}
              value={searchValue}
              onKeyPress={onValueKeyPress}
              placeholder={placeholder}
              onChange={onValueChange}
              className={'search-input'}
            />

            <button disabled={isEmptySearchValue} onClick={search} className={'search-button'}>
              Search
            </button>

            <span onClick={() => onCloseSearch(false)} className={'regular-link'}>
              Back
            </span>
            {suggestions.length > 0 && (
              <div className={'autocomplete-wrapper'} ref={suggestionTooltipRef}>
                <UsersAutocomplete
                  className={'moderation-user-search fixed'}
                  users={suggestions}
                  onUserClick={onSuggestionClick}
                />
              </div>
            )}
          </span>
        </>
      )}
    </span>
  );
};

export default Search;
